import React, { useEffect, useMemo, useState } from 'react';
import { Loader, Message } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { useEmployeePatoReport, usePatoRoster } from '../../hooks/useQueries';
import './PatoRosterModal.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

// Everyone's PATO balance as of the end of each pay period, as a chart and a
// table. The figures come from GET /payroll/pato-roster, which works them out
// from the same allowance and TimeCamp entries as the single-employee report.
//
// The chart is an emphasis chart, not one colour per person: with a whole
// roster on it, a hue each would run out of distinguishable colours long
// before it ran out of employees. Everyone is a grey line for context and one
// person - hovered, or pinned by clicking their row - is drawn in the accent
// on top. The table carries every exact number; the chart carries the shape.
//
// Clicking a row also opens it: a details row underneath, aligned to the same
// pay period columns, listing what was booked in each period.

const getRootCssVar = (name, fallback) => {
  try {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name);
    return String(value || '').trim() || fallback;
  } catch (_e) {
    return fallback;
  }
};

// "PPE 06/15" - pay period ending, month/day. Read off the date-only string so
// no timezone can move it to the day before.
const ppeLabel = (end) => {
  const match = String(end || '').match(/^\d{4}-(\d{2})-(\d{2})/);
  return match ? `PPE ${match[1]}/${match[2]}` : 'PPE';
};

const formatHours = (value) => {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

// What one pay period took, and what it credited. Both come from the server
// rather than from subtracting neighbouring balances: an adjustment and a day
// off in the same period would otherwise net into one misleading figure.
// Null when there is nothing to mark.
const usedAt = (emp, index) => {
  const hours = Number(emp.used?.[index]);
  return Number.isFinite(hours) && hours > 0 ? hours : null;
};
const creditedAt = (emp, index) => {
  const hours = Number(emp.credited?.[index]);
  return Number.isFinite(hours) && hours !== 0 ? hours : null;
};

const formatSigned = (value) => {
  const n = Number(value) || 0;
  return `${n < 0 ? '\u2212' : '+'}${formatHours(Math.abs(n))}`;
};

// A balance cell's text: the figure, or why there is not one.
const balanceText = (emp, index) => {
  if (emp.balances[index] != null) return formatHours(emp.balances[index]);
  return emp.unlimited?.[index] ? 'Unlimited' : '—';
};

// Day and month of a date-only string, e.g. "Jan 6". Built from the parts so
// no timezone can shift it.
const shortDate = (iso) => {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CREDIT_LABEL = {
  adjustment: 'Adjustment',
  // A mid-year policy change reads as an adjustment, as it does on the report.
  policy: 'Adjustment',
};

// The opened row's details: every time off and sick entry, and every
// adjustment or policy change, placed in the column of the pay period it falls
// in. Fetched only once the row is opened, from the single-employee report -
// the same ledger that produced the balances above.
function RosterDetailRow({ employee, year, periods }) {
  const query = useEmployeePatoReport(employee.id, year);
  const report = query.data;
  const columnCount = periods.length + 2;

  if (query.isLoading || query.isError || !report || report.usageKnown === false) {
    return (
      <tr className="pato-roster__detail-row">
        <td colSpan={columnCount} className="pato-roster__detail-status">
          {query.isLoading
            ? 'Loading entries from TimeCamp…'
            : query.error?.message || report?.unavailableReason || 'The entries could not be loaded.'}
        </td>
      </tr>
    );
  }

  // The opening grant is the Starting column, so it is not repeated here.
  const byPeriod = periods.map((period) => (report.events || []).slice(1)
    .filter((event) => event.date && event.date >= period.start && event.date <= period.end));

  return (
    <tr className="pato-roster__detail-row">
      <td className="pato-roster__sticky pato-roster__detail-label">Entries</td>
      <td className="pato-roster__detail-cell">
        <span className="pato-roster__detail-muted">
          {report.events?.[0]?.description || 'Company grant'}
        </span>
      </td>
      {byPeriod.map((events, index) => {
        const pato = events.filter((event) => event.kind === 'pato')
          .reduce((sum, event) => sum + Number(event.hours || 0), 0);
        const sick = events.filter((event) => event.kind === 'sick')
          .reduce((sum, event) => sum + Number(event.hours || 0), 0);
        const credit = events.filter((event) => event.credit != null)
          .reduce((sum, event) => sum + Number(event.credit || 0), 0);
        return (
          <td key={periods[index].end} className="pato-roster__detail-cell">
            {events.length === 0 ? (
              <span className="pato-roster__detail-muted">None</span>
            ) : (
              <>
                <div className="pato-roster__detail-sum">
                  {pato > 0 ? <span>Time off &minus;{formatHours(pato)}</span> : null}
                  {sick > 0 ? <span>Sick &minus;{formatHours(sick)}</span> : null}
                  {credit !== 0 ? <span>Credits {formatSigned(credit)}</span> : null}
                </div>
                {events.map((event) => (
                  <div
                    key={event.id || `${event.kind}-${event.date}-${event.hours ?? event.credit}`}
                    className="pato-roster__entry"
                    title={event.description || undefined}
                  >
                    <span className="pato-roster__entry-date">{shortDate(event.date)}</span>
                    <span className="pato-roster__entry-hours bdc-num">
                      {event.hours != null
                        ? <>&minus;{formatHours(event.hours)}</>
                        : event.credit != null ? formatSigned(event.credit) : ''}
                    </span>
                    {event.kind === 'sick' ? <span className="pato-roster__chip">Sick</span> : null}
                    {CREDIT_LABEL[event.kind] ? (
                      <span className="pato-roster__chip">{CREDIT_LABEL[event.kind]}</span>
                    ) : null}
                    {event.description ? (
                      <span className="pato-roster__entry-note">{event.description}</span>
                    ) : null}
                  </div>
                ))}
              </>
            )}
          </td>
        );
      })}
    </tr>
  );
}

export default function PatoRosterModal({ open, onClose }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  // The employee drawn in the accent: hover wins while it lasts, otherwise
  // whoever was pinned by a click.
  const [hoverId, setHoverId] = useState(null);
  const [pinnedId, setPinnedId] = useState(null);
  const activeId = hoverId ?? pinnedId;

  useEffect(() => {
    if (!open) return;
    setYear(new Date().getFullYear());
    setHoverId(null);
    setPinnedId(null);
  }, [open]);

  const query = usePatoRoster(year, { enabled: open });
  const report = query.data;
  const periods = useMemo(() => report?.periods || [], [report]);
  const employees = useMemo(() => report?.employees || [], [report]);
  const activeEmployee = employees.find((emp) => String(emp.id) === String(activeId)) || null;
  const hasInProgress = periods.some((period) => period.inProgress);

  const colors = useMemo(() => ({
    accent: getRootCssVar('--bdc-primary', '#007374'),
    context: getRootCssVar('--bdc-border-strong', '#c5c6cd'),
    grid: getRootCssVar('--bdc-border', '#e0e3e5'),
    zero: getRootCssVar('--bdc-text-faint', '#75777d'),
    text: getRootCssVar('--bdc-text-muted', '#45474c'),
  }), []);

  // Only people whose balances are known go on the chart; the table still
  // lists the rest, so nobody disappears.
  const charted = useMemo(() => employees.filter((emp) => emp.linked), [employees]);

  const chartData = useMemo(() => ({
    labels: periods.map((period) => ppeLabel(period.end)),
    datasets: charted.map((emp) => {
      const isActive = String(emp.id) === String(activeId);
      return {
        label: emp.name,
        employeeId: emp.id,
        data: emp.balances,
        borderColor: isActive ? colors.accent : colors.context,
        backgroundColor: isActive ? colors.accent : colors.context,
        borderWidth: 2,
        borderCapStyle: 'round',
        borderJoinStyle: 'round',
        pointRadius: isActive ? 4 : 0,
        pointHoverRadius: 5,
        pointBorderColor: getRootCssVar('--bdc-surface', '#ffffff'),
        pointBorderWidth: 2,
        // Lower draws later, so the highlighted line sits on top of the grey.
        order: isActive ? 0 : 1,
        tension: 0,
      };
    }),
  }), [periods, charted, activeId, colors]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    // The pointer only has to be nearest a line, never on its 2px.
    interaction: { mode: 'nearest', intersect: false, axis: 'xy' },
    onHover: (_event, elements) => {
      const datasetIndex = elements?.[0]?.datasetIndex;
      const id = datasetIndex == null ? null : charted[datasetIndex]?.id ?? null;
      setHoverId((prev) => (String(prev) === String(id) ? prev : id));
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: (items) => {
            const index = items?.[0]?.dataIndex;
            const period = periods[index];
            if (!period) return '';
            return period.inProgress ? `${ppeLabel(period.end)} (in progress)` : ppeLabel(period.end);
          },
          label: (context) => {
            const emp = charted[context.datasetIndex];
            const used = emp ? usedAt(emp, context.dataIndex) : null;
            const credited = emp ? creditedAt(emp, context.dataIndex) : null;
            const notes = [
              used ? `\u2212${formatHours(used)} used` : null,
              credited ? `${formatSigned(credited)} credited` : null,
            ].filter(Boolean);
            const extra = notes.length > 0 ? `  (${notes.join(', ')} this period)` : '';
            return `${formatHours(context.parsed?.y)} hrs${extra}  ${context.dataset.label}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: colors.text, maxRotation: 0, autoSkip: true },
      },
      y: {
        ticks: { color: colors.text, callback: (v) => `${v} h` },
        // Hairline grid, with zero drawn darker: below it means more PATO
        // has been taken than the year allowed.
        grid: {
          color: (ctx) => (ctx.tick?.value === 0 ? colors.zero : colors.grid),
          lineWidth: 1,
        },
        border: { display: false },
      },
    },
  }), [charted, periods, colors]);

  const togglePin = (id) => setPinnedId((prev) => (String(prev) === String(id) ? null : id));

  return (
    <Modal open={open} onClose={onClose} size="full" overflow>
      <Modal.Header>
        <Modal.Title>PATO report &mdash; all employees</Modal.Title>
      </Modal.Header>

      <Modal.Body onMouseLeave={() => setHoverId(null)}>
        <div className="pato-roster__years">
          <Button size="sm" icon="chevron-left" onClick={() => setYear((y) => y - 1)} aria-label="Previous year" />
          <span className="pato-roster__year bdc-num">{year}</span>
          <Button
            size="sm"
            icon="chevron-right"
            onClick={() => setYear((y) => y + 1)}
            disabled={year >= currentYear}
            aria-label="Next year"
          />
        </div>

        {query.isLoading ? (
          <Loader content="Loading balances from TimeCamp…" />
        ) : query.isError ? (
          <Message type="error" showIcon>
            {query.error?.message || 'The PATO report could not be loaded.'}
          </Message>
        ) : periods.length === 0 ? (
          <Message type="info" showIcon>No pay periods in {year} have started yet.</Message>
        ) : (
          <>
            <section className="pato-roster__chart-card">
              <div className="pato-roster__chart-head">
                <div className="pato-roster__chart-title">PATO balance at each pay period end</div>
                <div className="pato-roster__legend">
                  <span className="pato-roster__key">
                    <span className="pato-roster__swatch pato-roster__swatch--accent" />
                    {activeEmployee ? activeEmployee.name : 'Hover or select an employee'}
                  </span>
                  <span className="pato-roster__key">
                    <span className="pato-roster__swatch" />
                    {activeEmployee ? 'Everyone else' : 'Employees'}
                  </span>
                </div>
              </div>
              <div className="pato-roster__chart" onMouseLeave={() => setHoverId(null)}>
                <Line data={chartData} options={chartOptions} />
              </div>
            </section>

            <div className="nsi-table-wrap pato-roster__tablewrap">
              <table className="nsi-table pato-roster__table">
                <thead>
                  <tr>
                    <th className="pato-roster__sticky">Employee</th>
                    <th className="nsi-table__right">Starting</th>
                    {periods.map((period) => (
                      <th key={period.end} className="nsi-table__right" title={`${period.start} to ${period.end}`}>
                        {ppeLabel(period.end)}
                        {period.inProgress ? ' *' : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => {
                    // The opened row and the highlighted line are the same
                    // person: opening a row pins it on the chart.
                    const isPinned = String(emp.id) === String(pinnedId);
                    return (
                      <React.Fragment key={emp.id}>
                      <tr
                        className={`pato-roster__row${isPinned ? ' is-pinned' : ''}`}
                        onMouseEnter={() => emp.linked && setHoverId(emp.id)}
                        onMouseLeave={() => setHoverId(null)}
                        onClick={() => emp.linked && togglePin(emp.id)}
                        onKeyDown={(event) => {
                          if (!emp.linked) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            togglePin(emp.id);
                          }
                        }}
                        tabIndex={emp.linked ? 0 : undefined}
                        aria-expanded={emp.linked ? isPinned : undefined}
                      >
                        <td className="pato-roster__sticky">
                          {emp.linked ? (
                            <i
                              className={`bi bi-chevron-right pato-roster__chevron${isPinned ? ' is-open' : ''}`}
                              aria-hidden="true"
                            />
                          ) : (
                            <span className="pato-roster__chevron-space" aria-hidden="true" />
                          )}
                          <span className="pato-roster__name">{emp.name}</span>
                          {!emp.linked ? <span className="pato-roster__note">Not in TimeCamp</span> : null}
                        </td>
                        <td className="nsi-table__right bdc-num nsi-table__muted">
                          {emp.allowance == null ? 'Unlimited' : formatHours(emp.allowance)}
                        </td>
                        {emp.balances.map((value, index) => {
                          const used = usedAt(emp, index);
                          const credited = creditedAt(emp, index);
                          return (
                            <td
                              key={periods[index]?.end || index}
                              className={`nsi-table__right bdc-num${value != null && value < 0 ? ' bdc-text-bad' : ''}`}
                            >
                              {balanceText(emp, index)}
                              {/* What this period took off and credited, under
                                  the balance it left - so a change reads
                                  without subtracting across columns. */}
                              {used ? (
                                <span className="pato-roster__drop">&minus;{formatHours(used)}</span>
                              ) : null}
                              {credited ? (
                                <span className="pato-roster__drop pato-roster__drop--credit">
                                  {formatSigned(credited)}
                                </span>
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                      {isPinned ? <RosterDetailRow employee={emp} year={year} periods={periods} /> : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="text-muted small pato-roster__footnote">
              Balance after the company grant, every adjustment and policy change, and all PATO (time off
              and sick time) booked in TimeCamp up to and including each pay period end; the small figures
              under a balance are what that pay period used and credited. Close dates are not deducted. Below
              zero means more PATO was taken than was credited. Unlimited means unlimited time off was the
              company policy at that pay period end.
              {hasInProgress ? ' * Pay period in progress: its hours can still change until payroll has run.' : ''}
              {' '}Click a row to see each pay period&apos;s entries and keep that employee highlighted.
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
